import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Cart, CartUiEventAddToCart, OrderEntry, PromotionLocation } from '@spartacus/cart/base/root';
import { RoutingService } from '@spartacus/core';
import { FocusConfig, ICON_TYPE, LaunchDialogService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, switchMapTo, tap } from 'rxjs/operators';
import { UigActiveCartService } from 'src/app/services/uig-active-cart.service';
import { UigCartItemComponent } from '../../uig-cart-shared/uig-cart-item/uig-cart-item.component';
import { UigCartService } from '../../uig-cart.service';

@Component({
    selector: 'app-uig-added-to-cart-dialog',
    templateUrl: './uig-added-to-cart-dialog.component.html',
    styleUrls: ['./uig-added-to-cart-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class UigAddedToCartDialogComponent implements OnInit, OnDestroy {
  private removedEntry: boolean;

  constructor(
      protected activeCartService: UigActiveCartService,
      protected uigCartService: UigCartService,
      protected launchDialogService: LaunchDialogService,
      protected routingService: RoutingService,
      protected el: ElementRef
  ) {}
  iconTypes = ICON_TYPE;

  entry$: Observable<OrderEntry | undefined>;
  cart$: Observable<Cart> = this.activeCartService.getActive();
  loaded$: Observable<boolean> = this.activeCartService.isStable();
  addedEntryWasMerged$: Observable<boolean>;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  titleSubscription: Subscription = new Subscription();
  title: string;

  @ViewChild(UigCartItemComponent) private uigCartItemComponent: UigCartItemComponent;

  quantity = 0;

  form: UntypedFormGroup = new UntypedFormGroup({});

  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: 'button',
    focusOnEscape: true,
  };

  protected quantityControl$: Observable<UntypedFormControl>;

  protected subscription = new Subscription();

  @HostListener('click', ['$event'])
  handleClick(event: UIEvent): void {
    if ((event.target as any).tagName === this.el.nativeElement.tagName) {
      this.dismissModal('Cross click');
    }
  }

  ngOnInit(): void {
    this.subscription.add(
        this.launchDialogService.data$.subscribe(
            (dialogData: CartUiEventAddToCart) => {
              this.init(
                  dialogData.productCode,
                  dialogData.quantity,
                  dialogData.numberOfEntriesBeforeAdd
              );
              this.titleSubscription = this.loaded$.pipe(
                filter((loaded) => loaded),
                switchMapTo(this.activeCartService.getEntries()),
                map((entries) => entries.length === dialogData.numberOfEntriesBeforeAdd)
              ).subscribe((data) => {
                this.title = this.getAddedToCartDialogTitle(data);
              });
            }
        )
    );
    this.subscription.add(
        this.routingService
            .getRouterState()
            .pipe(filter((state) => !!state.nextState))
            .subscribe(() => this.dismissModal('dismiss'))
    );
  }

  /**
   * Returns an observable formControl with the quantity of the cartEntry,
   * but also updates the entry in case of a changed value.
   * The quantity can be set to zero in order to remove the entry.
   */
  getQuantityControl(): Observable<UntypedFormControl> {
    if (!this.quantityControl$) {
      this.quantityControl$ = this.entry$.pipe(
          filter((e) => !!e),
          map((entry) => this.getQuantityFormControl(entry)),
          switchMap(() =>
              this.form.valueChanges.pipe(
                  // eslint-disable-next-line import/no-deprecated
                  startWith(null),
                  tap((valueChange) => {
                    if (valueChange) {
                      this.activeCartService.updateEntry(
                          valueChange.entryNumber,
                          valueChange.quantity
                      );
                      if (valueChange.quantity === 0) {
                        this.dismissModal('Removed');
                      }
                    } else {
                      this.form.markAsPristine();
                    }
                  })
              )
          ),
          map(() => this.form.get('quantity') as UntypedFormControl),
          shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.quantityControl$;
  }

  init(
      productCode: string,
      quantity: number,
      numberOfEntriesBeforeAdd: number
  ): void {
    // Display last entry for new product code. This always corresponds to
    // our new item, independently of whether merging occured or not
    this.entry$ = this.activeCartService.getLastEntry(productCode);
    this.quantity = quantity;

  }

  /**
   * Adds quantity and entryNumber form controls to the FormGroup.
   * Returns quantity form control.
   */
  protected getQuantityFormControl(entry?: OrderEntry): UntypedFormControl {
    if (!this.form.get('quantity')) {
      const quantity = new UntypedFormControl(entry?.quantity, {
        updateOn: 'blur',
      });
      this.form.addControl('quantity', quantity);

      const entryNumber = new UntypedFormControl(entry?.entryNumber);
      this.form.addControl('entryNumber', entryNumber);
    } else {
      // set the real quantity added to cart
      this.form.get('quantity')?.setValue(entry?.quantity);
    }

    return this.form.get('quantity') as UntypedFormControl;
  }

  dismissModal(reason?: any): void {
    this.launchDialogService.closeDialog(reason);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  removeItem() {
    this.uigCartItemComponent.removeItem();
    this.removedEntry = true;
  }

  public getAddedToCartDialogTitle(merged: boolean): string {
    if (this.removedEntry) {
      return 'uigAddToCart.removedItem';
    }
    else if (this.uigCartService.hasAddToCartError()) {
      return 'addToCart.error';
    } else if (this.uigCartService.getLastEntryListedStatus() == false) {
      return 'uigAddToCart.itemsNotListedForUser';
    } else if (this.uigCartService.getIsRemovedEntry() == true) {
      return 'uigAddToCart.noStock';
    } else if (merged === true){
      return 'addToCart.itemsIncrementedInYourCart';
    } else {
      return 'addToCart.itemsAddedToYourCart';
    }
  }

}
